import { Pool } from '../iconPool';
import { SlotIcon } from '../slotIcon';
import EventsEmitter from '../eventsEmitter';

import { isMobile } from '../../helpers/isMobile';
import { addToGameLoop, removeFromGameLoop } from '../../helpers/gameLoop';

import { gameStore } from '../../../store/gameStore';

import { iconStates } from '../../constants/symbolsNames';
import { GAME_SPEED_TYPES } from '../../constants/gameConstants';
import { gameDrawer } from '../../../services/gameDrawer';

const START_SPEED = 0;
const STOPPING_STEPS = 10;
const REVERSE_STEPS_FAST_MODE = isMobile() ? 14 : 12;
const REVERSE_STEPS_STANDARD_MODE = isMobile() ? 7 : 8;

export class Reel extends EventsEmitter {
  constructor(
    index,
    slotSpinContainer,
    slotWinLayer,
    params,
    iconNumberProvider,
    isLastReel,
    Icon = SlotIcon
  ) {
    super();
    Object.assign(this, params);
    this.index = index;
    this.iconsHeight = params.iconsHeight;
    this._moveSpeed = 0;
    this.initStore(index, slotSpinContainer, slotWinLayer, params, Icon);
    this.time = null;
    this._direction = 1;
    this._finalDrawIcons = [];
    this._reelIcons = [];
    this.nextIconNumber = null;
    this.isLastReel = isLastReel;
    this.iconNumberProvider = iconNumberProvider;
    this.speedFactor = 1;
    this.startLoopCount = 7;
    this._stopping = false;
    this.matrix = [];
  }

  initStore(index, slotSpinContainer, slotWinLayer, params, Icon) {
    const xPosition =
      (Array.isArray(isMobile() ? params.mobileColumnPositions : params.webColumnPositions)
        ? isMobile()
          ? params.mobileColumnPositions[index]
          : params.webColumnPositions[index]
        : isMobile()
        ? params.mobileColumnPositions
        : params.webColumnPositions) || 0;

    this._iconsStore = new Pool((nextNumber) => {
      return new Icon(
        {
          parent: slotSpinContainer,
          elementId: `spinIcon_${index}_${nextNumber}`,
          winParent: slotWinLayer,
          styles: { x: xPosition },
          reelIndex: index,
          index: nextNumber,
        },
        index
      );
    });
  }

  startSpin(direction = 1) {
    this._direction = direction;
    this.resetReel();
    this._moveSpeed *= this._direction;
    this._spinning = true;
    this.iconNumberProvider.lastIconIndex = 0;
    addToGameLoop(this.spinLoop, this);
  }

  spinLoop() {
    if (this._spinLoopCounter === this.startLoopCount)
      this._moveSpeed = this._direction * this.columnsMoveSpeed;
    this._spinLoopCounter += 1;
    this.move(this._moveSpeed);
    if (!this.index && !this._reversing && this._moveSpeed && gameStore.isExpandWildOpen) {
      gameDrawer.moveExpandWildContainer();
    }
    this.takeOffObsoleteIcons();
    this.addIconIfNeed();
    if (this._stopping && this._finalDrawIcons.length > 0) {
      // get y position of outermost final icon
      const stopIconY =
        this._direction === 1
          ? this._finalDrawIcons[this._finalDrawIcons.length - 1].y
          : this._finalDrawIcons[0].y;
      const stopRowPosition =
        this._direction === 1
          ? this.finalPositions[this.finalPositions.length - 1]
          : this.finalPositions[0];
      // not allow it to pass behind reverse line
      const reverseLine =
        this._direction === 1
          ? stopRowPosition + this.reverseLineOffset
          : stopRowPosition - this.reverseLineOffset;
      if (
        this._direction === 1 &&
        this._reversing === false &&
        reverseLine - stopIconY < this._moveSpeed
      ) {
        this._moveSpeed = reverseLine - stopIconY;
      } else if (
        this._direction === -1 &&
        this._reversing === false &&
        reverseLine - stopIconY > this._moveSpeed
      ) {
        this._moveSpeed = reverseLine - stopIconY;
      }
      // when reached reverse line set reverse move speed
      if (
        this._reversing === false &&
        ((this._direction === 1 && stopIconY >= reverseLine) ||
          (this._direction === -1 && stopIconY <= reverseLine))
      ) {
        gameDrawer.handleReelSound(this.index);
        this._reversing = true;
        this._moveSpeed =
          -1 * this._direction * Math.abs((stopIconY - stopRowPosition) / this._reverseSteps);
      }
      if (this._reversing) {
        this._reverceLoops += 1;
        if (this._reverceLoops === this._reverseSteps) {
          this._moveSpeed = stopRowPosition - stopIconY;
        } else if (this._reverceLoops > this._reverseSteps) {
          this.finalActions();
        }
      }
    }
  }

  needTakeOffIcon(outermostIconIndex) {
    if (outermostIconIndex === undefined) {
      outermostIconIndex = this.getOutermostIconIndex();
      if (outermostIconIndex < 0) return false;
    }
    const outermostIcon = this._reelIcons[outermostIconIndex];
    return this._direction === 1
      ? outermostIcon.y - this.iconsHeight / 2 > this.bottomLine + this.takeOffLineOffset
      : outermostIcon.y + this.iconsHeight / 2 < this.topLine - this.takeOffLineOffset;
  }

  getOutermostIconIndex() {
    return this._direction === -1 ? (this._reelIcons.length ? 0 : -1) : this._reelIcons.length - 1;
  }

  takeOffObsoleteIcons() {
    let outermostIconIndex = this.getOutermostIconIndex();
    while (outermostIconIndex > -1 && this.needTakeOffIcon(outermostIconIndex)) {
      const icon = this._reelIcons.splice(outermostIconIndex, 1)[0];
      this._iconsStore.storeInstance(icon);
      outermostIconIndex = this.getOutermostIconIndex();
    }
  }

  stopSpin(forced = false) {
    if (this._stopping === false) {
      this._stopping = true;
      this.initFromResponseData();
      if (forced) {
        this._reverseSteps = isMobile() ? 8 : 14;
      }
    }
  }

  needAddIcon() {
    if (this.nextIconNumber === null) {
      let cardNumber;
      let finalCard;

      if (this._stopping) {
        cardNumber =
          this._direction === -1
            ? this.finalDrawCardNumbers.shift()
            : this.finalDrawCardNumbers.pop();
        finalCard = cardNumber !== undefined;
      }

      if (cardNumber === undefined) cardNumber = this.iconNumberProvider.getNext(this.index);
      this.nextIconNumber = cardNumber;
      this.isFinalIcon = finalCard;
    }

    const outermostIcon =
      this._direction === 1 ? this._reelIcons[0] : this._reelIcons[this._reelIcons.length - 1];
    const distance =
      this._getIconMargin(outermostIcon.iconNumber) +
      this._getIconMargin(this.nextIconNumber) -
      this.iconsHeight / 2;

    return this._direction === 1
      ? outermostIcon.y - distance > this.topLine
      : outermostIcon.y + distance < this.bottomLine;
  }

  iconsToSpinContainer() {
    this._reelIcons.forEach((i) => {
      i.changeState(iconStates.idle);
      // i.toSpinParent();
    });
  }

  addIconIfNeed() {
    if (this.needAddIcon()) {
      const iconState = this._stopping
        ? iconStates.idle
        : this._spinning
        ? iconStates.blur
        : iconStates.idle;

      const icon = this.addToReelIcons(this.nextIconNumber, iconState, this._direction === 1);
      if (this._stopping && this.isFinalIcon) {
        this._direction === -1
          ? this._finalDrawIcons.push(icon)
          : this._finalDrawIcons.unshift(icon);
      }
    }
  }

  move(move) {
    this._reelIcons.forEach((spinIcon) => {
      spinIcon.alpha = 1;
      spinIcon.y += move;
    });
  }

  _getIconMargin(cardNumber) {
    return this.iconMargins[cardNumber.toString()] || this.iconMargins.default;
  }

  getRowIcon(responseRow) {
    return this._finalDrawIcons[responseRow];
  }

  fullUpdateFromResponseData(matrix = []) {
    this._reelIcons.forEach((i) => this._iconsStore.storeInstance(i));
    this._reelIcons.length = 0;
    this._finalDrawIcons.length = 0;
    this.initFromResponseData();
    this.finalDrawCardNumbers = [...matrix];
    this.finalDrawCardNumbers.forEach((cardNumber) => {
      const i = this.addToReelIcons(cardNumber, iconStates.idle, false);
      this._finalDrawIcons.push(i);
    });
    this.finalPositions.forEach((position, index) => {
      this._reelIcons[index].y = position;
    });
  }

  addToReelIcons(cardNumber, iconState, toTop = true) {
    const icon = this._iconsStore.getInstance();
    if (toTop) {
      const topIcon = this._reelIcons[0];
      if (topIcon) {
        icon.y =
          topIcon.y - this._getIconMargin(cardNumber) - this._getIconMargin(topIcon.iconNumber);
      }
      this._reelIcons.unshift(icon);
    } else {
      const bottomIcon = this._reelIcons[this._reelIcons.length - 1];
      if (bottomIcon) {
        icon.y =
          bottomIcon.y +
          this._getIconMargin(cardNumber) +
          this._getIconMargin(bottomIcon.iconNumber);
      }
      this._reelIcons.push(icon);
    }
    this.nextIconNumber = null;
    icon.show();
    icon.changeIcon(cardNumber, iconState);
    return icon;
  }

  finalActions() {
    const promises = [];
    this._finalDrawIcons.forEach((icon, index) => {
      icon.y = this.finalPositions[index];
    });
    removeFromGameLoop(this.spinLoop, this);
    Promise.all(promises).then(() => {
      this.finalTransitionsBetweenIconStores();
      this._spinning = false;
      gameDrawer.handleStopReelAnimation();
    });
  }

  initFromResponseData() {
    const { matrix } = gameStore.spinResult;
    const responseData = matrix && matrix[this.index];
    if (!responseData) return;
    this.finalDrawCardNumbers = [...responseData];
  }

  resetReel() {
    this.matrix = [];
    this._stopping = false;
    this._spinLoopCounter = 0;
    this._moveSpeed = START_SPEED;
    this._reverceLoops = 0;
    this._reversing = false;
    this._brakeSteps = STOPPING_STEPS;
    this._finalDrawCardNumbers = [];
    this._finalDrawIcons = [];
    this.reverseLineOffset = isMobile() ? 15 : 35;
    this._reverseSteps = GAME_SPEED_TYPES.standard
      ? REVERSE_STEPS_FAST_MODE
      : REVERSE_STEPS_STANDARD_MODE;
    this.nextIconNumber = null;
    this.isFinalIcon = false;
  }

  finalTransitionsBetweenIconStores() {
    this._reelIcons = this._reelIcons.filter((icon) => {
      if (this._finalDrawIcons.indexOf(icon) === -1) {
        this._iconsStore.storeInstance(icon);
        return false;
      }
      return true;
    });
  }

  resetIcons() {
    this._reelIcons.forEach((icon) => {
      icon.changeState(iconStates.idle);
    });
  }

  getSpecialIconIfExists(iconNumber) {
    // eslint-disable-next-line no-restricted-syntax
    for (const icon of this._reelIcons) {
      if (icon.iconNumber === iconNumber) return icon;
    }
    return null;
  }

  show() {
    this._reelIcons.forEach((i) => i.show());
  }

  hide() {
    this._reelIcons.forEach((i) => i.hide());
  }

  resetSpeed() {
    this.speedFactor = 1;
    this._iconsStore.forEach((ic) => {
      ic.setSpeedFactor(this.speedFactor);
    });
  }
}
